// Import new API service and utilities
import apiService from '../../../lib/services/apiService';
import { 
  getDefaultStartDate, 
  getDefaultEndDate, 
  parseDateFromAPI, 
  formatDateForDisplay 
} from '../../../lib/utils/dateUtils';

// Firebase Functions base URL (for legacy endpoints still in use)
const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-gla-platform-2024.cloudfunctions.net';

/**
 * Get unique distributors for filter dropdown
 */
export const getDistributors = async () => {
  try {
    console.log('🏢 Fetching distributors for filters...');
    const response = await apiService.get('/api/v1/imdb/companies/distributors');
    
    // Helper function to extract company names from various formats
    const extractCompanyNames = (data) => {
      if (!data) return [];
      
      // If it's already an array of strings, return it
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
        return data;
      }
      
      // If it's an array of objects with company_name property
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        return data
          .map(item => item.company_name || item.name || item.distributor || item)
          .filter(Boolean)
          .filter(name => typeof name === 'string');
      }
      
      return [];
    };
    
    // Handle different response formats
    if (response.success && response.data) {
      const companies = extractCompanyNames(response.data);
      if (companies.length > 0) {
        return companies.sort();
      }
      
      // Try nested properties
      if (response.data.companies) {
        const companies = extractCompanyNames(response.data.companies);
        if (companies.length > 0) return companies.sort();
      }
      if (response.data.distributors) {
        const companies = extractCompanyNames(response.data.distributors);
        if (companies.length > 0) return companies.sort();
      }
    }
    
    // Fallback: if response is directly an array
    if (Array.isArray(response)) {
      const companies = extractCompanyNames(response);
      if (companies.length > 0) return companies.sort();
    }
    
    console.warn('⚠️ Unexpected response format from distributors endpoint:', response);
    return [];
    
  } catch (error) {
    console.error('Error fetching distributors:', error);
    return [];
  }
};

/**
 * Get unique production companies for filter dropdown
 */
export const getProductionCompanies = async () => {
  try {
    console.log('🏢 Fetching production companies for filters...');
    const response = await apiService.get('/api/v1/imdb/companies/producers');
    
    // Helper function to extract company names from various formats
    const extractCompanyNames = (data) => {
      if (!data) return [];
      
      // If it's already an array of strings, return it
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
        return data;
      }
      
      // If it's an array of objects with company_name property
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        return data
          .map(item => item.company_name || item.name || item.producer || item)
          .filter(Boolean)
          .filter(name => typeof name === 'string');
      }
      
      return [];
    };
    
    // Handle different response formats
    if (response.success && response.data) {
      const companies = extractCompanyNames(response.data);
      if (companies.length > 0) {
        return companies.sort();
      }
      
      // Try nested properties
      if (response.data.companies) {
        const companies = extractCompanyNames(response.data.companies);
        if (companies.length > 0) return companies.sort();
      }
      if (response.data.producers) {
        const companies = extractCompanyNames(response.data.producers);
        if (companies.length > 0) return companies.sort();
      }
    }
    
    // Fallback: if response is directly an array
    if (Array.isArray(response)) {
      const companies = extractCompanyNames(response);
      if (companies.length > 0) return companies.sort();
    }
    
    console.warn('⚠️ Unexpected response format from producers endpoint:', response);
    return [];
    
  } catch (error) {
    console.error('Error fetching production companies:', error);
    return [];
  }
};

/**
 * Get films releasing this week (next 7 days)
 * For the home page "This Week" widget
 */
export const getFilmsReleasingThisWeek = async () => {
  try {
    console.log('📅 Fetching films releasing this week...');
    
    // Calculate date range for next 7 days
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    
    const startDate = today.toISOString().split('T')[0];
    const endDate = nextWeek.toISOString().split('T')[0];
    
    const overview = await getFilmTrackingOverview({
      startDate,
      endDate,
      sortBy: 'release_date',
      sortOrder: 'asc',
      limit: 200
    });

    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);

    const endOfWindow = new Date(startOfToday);
    endOfWindow.setDate(endOfWindow.getDate() + 7);

    const filmsInWindow = (overview.films || []).filter((film) => {
      const releaseDateStr = film.release_date || film.release_date_iso;
      const releaseDateObj = parseDateFromAPI(releaseDateStr);
      if (!releaseDateObj) return false;
      return releaseDateObj >= startOfToday && releaseDateObj < endOfWindow;
    });

    filmsInWindow.sort((a, b) => {
      const dateA = parseDateFromAPI(a.release_date || a.release_date_iso);
      const dateB = parseDateFromAPI(b.release_date || b.release_date_iso);
      if (!dateA || !dateB) return 0;
      return dateA.getTime() - dateB.getTime();
    });

    const enrichedFilms = filmsInWindow.map((film) => ({
      ...film,
      film_title: film.film_title || film.imdb_title || film.title_text,
      pct_aware: film.pct_aware ?? film.awareness_pct ?? 0,
      pct_interest: film.pct_interest ?? film.high_interest_pct ?? 0,
      pct_theater: film.pct_theater ?? film.theater_intent_pct ?? 0,
      survey_complete_date: film.survey_complete_date || film.survey_date,
      release_date_display:
        film.release_date_display || (film.release_date ? formatDateForDisplay(film.release_date) : null)
    }));
    
    console.log(`✅ Found ${enrichedFilms.length} films releasing this week`);
    return { films: enrichedFilms, metadata: overview.metadata || {} };
    
  } catch (error) {
    console.error('Error fetching this week\'s films:', error);
    // Return empty data structure to prevent crashes
    return { films: [], metadata: {} };
  }
};

/**
 * Get film tracking overview data from new API with filtering options
 */
export const getFilmTrackingOverview = async (filters = {}) => {
  try {
    // Set up default parameters
    const params = {
      release_date_from: filters.startDate || getDefaultStartDate(),
      release_date_to: filters.endDate || getDefaultEndDate(),
      sort_by: filters.sortBy || 'release_date',
      sort_order: filters.sortOrder || 'asc',
      limit: filters.limit || 100,
      offset: filters.offset || 0
    };

    // Add optional filters if provided
    if (filters.imdbId) params.imdb_id = filters.imdbId;
    if (filters.weeksUntilReleaseMin !== undefined) params.weeks_until_release_min = filters.weeksUntilReleaseMin;
    if (filters.weeksUntilReleaseMax !== undefined) params.weeks_until_release_max = filters.weeksUntilReleaseMax;
    
    // Add company filters (when backend implements)
    if (filters.distributors && filters.distributors.length > 0) {
      params.distributor_ids = filters.distributors.join(',');
    }
    if (filters.productionCompanies && filters.productionCompanies.length > 0) {
      params.production_company_ids = filters.productionCompanies.join(',');
    }

    const response = await apiService.getFilmTrackingSummary(params);
    
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch film tracking overview');
    }
    
    const normalizeDateValue = (value) => {
      if (!value) return null;
      if (typeof value === 'string') {
        return value.split('T')[0];
      }
      if (value?.value) {
        return normalizeDateValue(value.value);
      }
      if (value?.raw) {
        return normalizeDateValue(value.raw);
      }
      return null;
    };

    // Transform the API data to match existing UI expectations
    const transformedData = response.data.map(film => {
      const releaseDate = normalizeDateValue(film.release_date);
      const surveyDate = normalizeDateValue(film.survey_complete_date);
      const releaseDateObj = parseDateFromAPI(releaseDate);

      const toPercentage = (val) => {
        if (val === null || val === undefined) return 0;
        const numeric = Number(val);
        if (Number.isNaN(numeric)) return 0;
        // Handle pre-percent vs decimal values
        if (numeric <= 1 && numeric >= 0) {
          return Math.round(numeric * 100);
        }
        return Math.round(numeric);
      };

      return {
        // Core identifiers
        tt_id: film.imdb_id,
        imdb_id: film.imdb_id,
        film_title: film.title_text,
        imdb_title: film.title_text,
        
        // Dates and timing
        release_date: releaseDate,
        release_date_iso: releaseDate,
        release_date_obj: releaseDateObj,
        release_date_display: releaseDate ? formatDateForDisplay(releaseDate) : null,
        survey_date: surveyDate,
        last_survey_date: surveyDate,
        current_weeks_until_release: film.current_weeks_until_release,
        
        // Core metrics (convert decimals to percentages)
        awareness_pct: toPercentage(film.pct_aware),
        high_interest_pct: toPercentage(film.pct_interest),
        theater_intent_pct: toPercentage(film.pct_theater),
        willingness_to_pay_pct: toPercentage(film.pct_willing_to_pay),
        pct_aware: toPercentage(film.pct_aware),
        pct_interest: toPercentage(film.pct_interest),
        pct_theater: toPercentage(film.pct_theater),
        pct_willing_to_pay: toPercentage(film.pct_willing_to_pay),
        
        // New metrics from API
        heat_pct: toPercentage(film.pct_heat),
        plf_pct: toPercentage(film.pct_plf),
        interest_of_aware_pct: toPercentage(film.pct_interest_of_aware),
        interest_not_aware_pct: toPercentage(film.pct_interest_not_aware),
        
        // Survey data
        total_responses: film.total_responses,
        unique_participants: film.total_responses, // Use total responses as participant count
        survey_complete_date: surveyDate,
        
        // Visual assets
        poster_url: film.primary_image_url,
        primary_image_url: film.primary_image_url,
        
        // Data source information (all from new API)
        data_source: 'api',
        isDecipherData: false,
        isQuorumData: false,
        dataSourceLabel: 'Current Survey Data',
        dataSourceColor: '#2e7d32', // Green for new API data
        
        // Release timing helpers
        weeksUntilRelease: film.current_weeks_until_release,
        releaseStatus: film.current_weeks_until_release > 0 ? 'upcoming' : 
                     film.current_weeks_until_release === 0 ? 'this-week' : 'released',
        
        // Survey timing
        survey_period: surveyDate ? formatDateForDisplay(surveyDate) : null,
        
        // Enhanced data flag
        hasEnhancedData: true, // All new API data is considered enhanced
        
        // Additional computed fields
        isUpcoming: film.current_weeks_until_release >= 0,
        releaseText: film.current_weeks_until_release > 0 ? 
          `${film.current_weeks_until_release} weeks` :
          film.current_weeks_until_release === 0 ? 'This week' : 
          `${Math.abs(film.current_weeks_until_release)} weeks ago`
      };
    });
    
    // Return both the transformed data and metadata
    return {
      films: transformedData,
      metadata: response.metadata,
      pagination: response.pagination,
      totalFilms: response.pagination?.total || transformedData.length
    };
  } catch (error) {
    console.error('Error fetching film tracking overview:', error);
    throw error;
  }
};

/**
 * Get comprehensive film details using new API endpoints
 */
export const getFilmDetails = async (imdbId, filters = {}) => {
  try {
    console.log('🔍 Fetching comprehensive film details for:', imdbId);
    
    // Parallel API calls for better performance
    const [surveyResponse, imdbResponse] = await Promise.all([
      apiService.getFilmSurveyToplines(imdbId, filters),
      apiService.getIMDBMetadata(imdbId).catch(error => {
        console.warn('IMDB metadata failed, continuing without it:', error.message);
        return null;
      })
    ]);
    
    if (!surveyResponse.success || !surveyResponse.data) {
      throw new Error('Failed to fetch film survey data');
    }
    
    // Sort by FilmRecencyOrder from the API (1 = most recent, 2 = second most recent, etc.)
    // The API calculates this correctly, so we trust it rather than recalculating
    const sortedData = [...surveyResponse.data].sort((a, b) => {
      const recencyA = a.FilmRecencyOrder ?? 999;
      const recencyB = b.FilmRecencyOrder ?? 999;
      return recencyA - recencyB; // Ascending: 1, 2, 3...
    });
    
    console.log('✅ Film details fetched successfully:', {
      imdbId,
      surveyWaves: sortedData.length,
      hasIMDBData: !!imdbResponse,
      waves: sortedData.map(w => ({
        recency: w.FilmRecencyOrder,
        weeksUntilRelease: w.CurrentWeeksUntilRelease,
        date: w.SurveyCompleteDate,
        responses: w.totalResponses || w.TotalResponses
      }))
    });
    
    return {
      survey: {
        ...surveyResponse,
        data: sortedData
      },
      imdb: imdbResponse,
      imdbId
    };
    
  } catch (error) {
    console.error('Error fetching film details:', error);
    throw error;
  }
};

/**
 * Get Film Tracking statistics from dashboard API (preferred method)
 */
export const getDashboardStatsForFilmTracking = async () => {
  try {
    console.log('📊 Fetching Film Tracking stats from dashboard API...');
    
    const dashboardResponse = await apiService.getRawDashboardCounts();
    
    if (!dashboardResponse.success) {
      throw new Error('Dashboard API failed');
    }
    
    const data = dashboardResponse.data;
    const metadata = dashboardResponse.metadata || {};
    
    // Map dashboard fields to Film Tracking expectations
    const stats = {
      // Core statistics from dashboard API
      decipher_total_responses: data.TotalFilmTrackingInterviews || 0,
      decipher_total_participants: data.TotalFilmTrackingUniqueParticipants || 0,
      decipher_total_waves: data.TotalFilmTrackingSurveyWaves || 0,
      
      // Handle date fields that come as objects with .value property
      last_survey_date: data.LastSurveyDate?.value || data.LastSurveyDate,
      farthest_release_date: data.TrackingThroughDate?.value || data.TrackingThroughDate,
      
      // Film counts (from dashboard)
      total_films: data.TrackedFilms || 0,
      
      // Additional metadata
      data_source: data.DataSource || 'dashboard_api',
      last_refresh: data.LastRefresh,
      data_as_of: data.DataAsOf,
      
      // Performance indicators
      performance_mode: metadata.performance || 'normal',
      refresh_timestamp: metadata.refresh_timestamp,
      
      // Keep compatibility fields
      upcoming_films: 0, // Not available from dashboard
      released_films: 0, // Not available from dashboard
      avg_awareness: 0, // Not available from dashboard
      avg_interest: 0, // Not available from dashboard
      avg_theater_intent: 0, // Not available from dashboard
      last_updated: new Date().toISOString()
    };
    
    // Add debugging to verify data mapping
    console.log('🔍 Dashboard API raw response:', {
      TotalFilmTrackingInterviews: data.TotalFilmTrackingInterviews,
      TotalFilmTrackingUniqueParticipants: data.TotalFilmTrackingUniqueParticipants,
      TotalFilmTrackingSurveyWaves: data.TotalFilmTrackingSurveyWaves,
      LastSurveyDate: data.LastSurveyDate,
      TrackingThroughDate: data.TrackingThroughDate
    });
    
    console.log('✅ Dashboard stats mapped successfully:', {
      decipher_total_responses: stats.decipher_total_responses,
      decipher_total_participants: stats.decipher_total_participants,
      decipher_total_waves: stats.decipher_total_waves,
      last_survey_date: stats.last_survey_date,
      farthest_release_date: stats.farthest_release_date
    });
    
    return stats;
    
  } catch (error) {
    console.error('❌ Dashboard API failed, falling back to calculated stats:', error.message);
    // Fallback to existing calculated stats
    return getFilmTrackingStatsCalculated();
  }
};

/**
 * Get summary statistics by calculating from film data (fallback method)
 */
export const getFilmTrackingStatsCalculated = async () => {
  try {
    console.log('📊 Calculating Film Tracking stats from film data (fallback)...');
    
    // Get overview data to calculate stats
    const overviewResponse = await getFilmTrackingOverview();
    const films = overviewResponse.films || [];
    
    // Calculate aggregate statistics
    const totalFilms = films.length;
    const totalResponses = films.reduce((sum, film) => sum + (film.total_responses || 0), 0);
    const totalParticipants = totalResponses; // Using total responses as participant count
    
    // Find date ranges
    const surveyDates = films
      .map(film => parseDateFromAPI(film.survey_date))
      .filter(date => date !== null)
      .sort((a, b) => b.getTime() - a.getTime());
    
    const releaseDates = films
      .map(film => parseDateFromAPI(film.release_date))
      .filter(date => date !== null)
      .sort((a, b) => b.getTime() - a.getTime());
    
    const lastSurveyDate = surveyDates.length > 0 ? surveyDates[0] : null;
    const farthestReleaseDate = releaseDates.length > 0 ? releaseDates[0] : null;
    
    // Calculate unique survey periods (waves)
    const uniqueSurveyDates = [...new Set(films.map(film => 
      film.survey_date?.value || film.survey_date
    ))].filter(Boolean);
    
    const stats = {
      // Core statistics
      decipher_total_responses: totalResponses,
      decipher_total_participants: totalParticipants,
      decipher_total_waves: uniqueSurveyDates.length,
      
      // Date information
      last_survey_date: lastSurveyDate?.toISOString().split('T')[0] || null,
      farthest_release_date: farthestReleaseDate?.toISOString().split('T')[0] || null,
      
      // Film counts
      total_films: totalFilms,
      upcoming_films: films.filter(film => film.isUpcoming).length,
      released_films: films.filter(film => !film.isUpcoming).length,
      
      // Additional insights
      avg_awareness: totalFilms > 0 ? Math.round(films.reduce((sum, film) => sum + (film.awareness_pct || 0), 0) / totalFilms) : 0,
      avg_interest: totalFilms > 0 ? Math.round(films.reduce((sum, film) => sum + (film.high_interest_pct || 0), 0) / totalFilms) : 0,
      avg_theater_intent: totalFilms > 0 ? Math.round(films.reduce((sum, film) => sum + (film.theater_intent_pct || 0), 0) / totalFilms) : 0,
      
      // Data freshness
      data_source: 'calculated_fallback',
      performance_mode: 'calculated',
      last_updated: new Date().toISOString()
    };
    
    console.log('✅ Calculated stats generated:', stats);
    return stats;
    
  } catch (error) {
    console.error('Error calculating film tracking stats:', error);
    // Return empty fallback stats
    return {
      decipher_total_responses: 0,
      decipher_total_participants: 0,
      decipher_total_waves: 0,
      last_survey_date: null,
      farthest_release_date: null,
      total_films: 0,
      upcoming_films: 0,
      released_films: 0,
      avg_awareness: 0,
      avg_interest: 0,
      avg_theater_intent: 0,
      data_source: 'fallback_empty',
      performance_mode: 'error',
      last_updated: new Date().toISOString()
    };
  }
};

/**
 * Get summary statistics for the film tracking system (main entry point)
 * Uses dashboard API first, falls back to calculated stats if needed
 */
export const getFilmTrackingStats = async () => {
  // Use dashboard API as primary method
  return getDashboardStatsForFilmTracking();
};

/**
 * Get enhanced demographic breakdown for a specific film
 */
export const getDemographicBreakdown = async (ttId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/getDemographicBreakdown?ttId=${encodeURIComponent(ttId)}`);
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch demographic breakdown');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error fetching demographic breakdown:', error);
    throw error;
  }
};

/**
 * Get enhanced behavioral insights for a specific film
 */
export const getBehavioralInsights = async (ttId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/getBehavioralInsights?ttId=${encodeURIComponent(ttId)}`);
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch behavioral insights');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error fetching behavioral insights:', error);
    throw error;
  }
};

/**
 * Get enhanced geographic insights for a specific film
 */
export const getGeographicInsights = async (ttId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/getGeographicInsights?ttId=${encodeURIComponent(ttId)}`);
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch geographic insights');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error fetching geographic insights:', error);
    throw error;
  }
};

/**
 * Get enhanced open-ended analysis for a specific film
 */
export const getOpenEndedAnalysis = async (ttId) => {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/getOpenEndedAnalysis?ttId=${encodeURIComponent(ttId)}`);
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch open-ended analysis');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error fetching open-ended analysis:', error);
    throw error;
  }
};
